import { useQuery } from '@apollo/client';
import Grid from '@mui/material/Grid2';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { AdvancedFilters, FilterKeys } from '@/components/Filters';
import {
  ASSETS,
  BASINS,
  COMPANIES,
  COUNTRIES,
  DEVELOPMENT_STATUSES_CHECK_LIST,
  DEVELOPMENT_TYPES_CHECK_LIST,
  GEOLOGIES,
  HYDROCARBONS,
  NPV_MIN_MAX,
  PRODUCTION_MIN_MAX,
  RESERVES_MIN_MAX,
  RESERVOIR_MIN_MAX,
  SHORE_STATUSES_CHECK_LIST,
} from '@/components/Filters/filterTypes';
import {
  COMPANIES_QUERY,
  COUNTRIES_QUERY,
  DEVELOPMENT_STATUS_WITH_FILTER_QUERY,
  DEVELOPMENT_TYPE_QUERY,
  SHORE_STATUS_QUERY,
} from '@/components/Filters/operations';
import Support from '@/components/Support';
import { Feature } from '@/constants/features';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { usePageTutorial } from '@/hooks/usePageTutorial';
import { analytics, dimensions } from '@/utils/analytics';
import { getUserId } from '@/utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '@/utils/constants';
import {
  getFulltextResultParametersFromSearch,
  useGlobalSearchParameters,
} from '@/utils/globalSearch';
import AssetList from './AssetList';
import { ASSET_QUERY } from './AssetList/operations';
import { VALUATIONS_CONTEXT, VALUATIONS_GOOGLE_ANALYTICS_TAG } from './constants';
import Filters from './Filters';
import Sidebar from './Sidebar';
import { valuationsTutorialSteps } from './tutorialSteps';
import ValuationReports from './ValuationReports';

export const defaultFilters = {
  [FilterKeys.npvMin]: '',
  [FilterKeys.npvMax]: '',
  [FilterKeys.reservesMin]: '',
  [FilterKeys.reservesMax]: '',
  [FilterKeys.productionMin]: '',
  [FilterKeys.productionMax]: '',
  [FilterKeys.developmentStatuses]: [],
  // [FilterKeys.Formation]: [],
  [FilterKeys.developmentTypes]: [],
  [FilterKeys.depthMin]: '',
  [FilterKeys.depthMax]: '',
  [FilterKeys.assets]: [],
  [FilterKeys.countries]: [],
  [FilterKeys.companies]: [],
  [FilterKeys.basins]: [],
  [FilterKeys.shoreStatuses]: [],
  [FilterKeys.hydrocarbons]: [],
  [FilterKeys.includeHistoricalParticipants]: false,
  [FilterKeys.operator]: false,
  [FilterKeys.nonOperator]: false,
  [FilterKeys.companyOperand]: 'OR',
  [FilterKeys.opportunities]: false,
};

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.sizes.fullPage,
    flexWrap: 'nowrap',
  },
  filterItemAdvanced: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  valuationsMain: {
    display: 'flex',
    flexDirection: 'row',
    minHeight: 0,
    borderTop: `solid ${theme.palette.baseColors.iceBlue.c100} thin`,
  },
  valuationsCenterContainer: {
    flexGrow: 1,
    overflow: 'auto',
    minHeight: '100%',
    width: '60%',
    ...theme.palette.primaryBackground,
    ...theme.palette.scrollbar,
  },
  valuationsContainer: {
    flexGrow: 1,
    width: '20%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));

const getDefaultFilters = () => {
  const filters = JSON.parse(sessionStorage.getItem(VALUATIONS_CONTEXT));
  if (
    filters &&
    Object.keys(defaultFilters).every(filter => Object.keys(filters).includes(filter))
  ) {
    return filters;
  }
  return defaultFilters;
};

const Valuations = () => {
  const classes = useStyles();
  const { flagsLoaded, inDevelopment, geologyFilter } = useFeatureFlags();
  const [selectedAsset, setAsset] = useState(null);
  const [filters, setFilterState] = useState(getDefaultFilters());
  const [filterChanged, setFilterChanged] = useState(true);
  const [watchList, setWatchList] = useState(false);
  const [updateWatchList, setUpdateWatchList] = useState(false);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState(defaultFilters);
  const [advancedFilterTypes, setAdvancedFilterTypes] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [developmentStatuses, setDevelopmentStatuses] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const { data: shoreStatusData } = useQuery(SHORE_STATUS_QUERY, {
    onCompleted: data => {
      defaultFilters[FilterKeys.shoreStatuses] = data.shoreStatuses || [];
    },
  });

  useQuery(DEVELOPMENT_STATUS_WITH_FILTER_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      countries: selectedCountries,
    },
    onCompleted: data => {
      // Remove selected development status filters that are not in the new list:
      const developmentStatus = data.developmentStatusWithFilter || [];
      const filteredDevelopmentStatus = advancedFilters[FilterKeys.developmentStatuses].filter(
        status => developmentStatus.find(s => s.id === status.id && !s.isDisabled),
      );
      setAdvancedFilters({
        ...advancedFilters,
        [FilterKeys.developmentStatuses]: filteredDevelopmentStatus,
      });
      setDevelopmentStatuses(developmentStatus);
    },
  });

  const { data: developmentTypeData } = useQuery(DEVELOPMENT_TYPE_QUERY, {
    onCompleted: data => {
      defaultFilters[FilterKeys.developmentTypes] = data.developmentTypes || [];
    },
  });

  const setFilters = newFilters => {
    sessionStorage.setItem(VALUATIONS_CONTEXT, JSON.stringify(newFilters));
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: VALUATIONS_GOOGLE_ANALYTICS_TAG,
      event_action: ANALYTICS_EVENT_ACTIONS.filter,
      event_label: analytics(newFilters),
      userId: getUserId(),
      ...dimensions(newFilters, VALUATIONS_CONTEXT),
    });
    setFilterState(newFilters);
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const searchParams = useGlobalSearchParameters();
  const gloSearchParams = getFulltextResultParametersFromSearch(searchParams);

  const { data: countriesData } = useQuery(COUNTRIES_QUERY);
  const { data: companiesData } = useQuery(COMPANIES_QUERY);
  const { data: assetData } = useQuery(ASSET_QUERY, {
    variables: {
      legacyId: gloSearchParams.legacyId,
      countryIsoCode: gloSearchParams.isoCode,
    },
  });

  // Set global search and URL params to filters
  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    const country = countriesData?.countries?.find(c => c.isoCode === gloSearchParams.isoCode);
    const company = companiesData?.companies?.find(c => c.name === gloSearchParams.name);

    if (country && gloSearchParams.entity === 'COUNTRY') {
      const newFilters = { ...filters };
      newFilters[FilterKeys.countries] = [{ ...country, isChosen: true }];
      setFilters(newFilters);
    }

    // TODO: Check if we still need asset for the global search here
    // Assets in global search route to the asset template page
    if (country && assetData && gloSearchParams.entity === 'ASSET') {
      const asset = {
        country: { ...assetData.asset.country, name: country.name },
        displayName: assetData.asset.displayName,
        id: assetData.asset.id,
        legacyId: assetData.asset.legacyId,
        name: assetData.asset.name,
        __typename: assetData.asset.__typename,
      };

      defaultFilters[FilterKeys.assets] = [asset];
    }

    if (company && gloSearchParams.entity === 'COMPANY') {
      defaultFilters[FilterKeys.companies] = [company];
    }
  }, [countriesData, companiesData, assetData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    // Set the selected country iso codes, which will trigger a refetch of the development status:
    const countries = advancedFilters[FilterKeys.countries];
    if (countries?.length !== selectedCountries.length) {
      setSelectedCountries(countries.map(c => c.isoCode));
    }
  }, [advancedFilters]);
  usePageTutorial({
    steps: valuationsTutorialSteps,
    disable: !selectedAsset,
    feature: Feature.ValuationsPage,
    waitForElements: {
      ids: ['valuations-explore-more', 'valuations-country-downloads'],
      withinId: 'valuations-side-panel',
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: afraid to add missing deps and break something
  useEffect(() => {
    if (inDevelopment && geologyFilter) {
      setAdvancedFilterTypes([COUNTRIES, ASSETS, COMPANIES, BASINS, HYDROCARBONS, GEOLOGIES]);
    } else {
      setAdvancedFilterTypes([COUNTRIES, ASSETS, COMPANIES, BASINS, HYDROCARBONS]);
    }
  }, [flagsLoaded]);

  const handleAdvancedFiltersClick = () => {
    setAdvancedFilters(filters);
    setFiltersModalOpen(true);
  };

  return (
    <Grid className={classes.root} container direction='column'>
      <ErrorBoundary>
        <Filters
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          setWatchList={bool => {
            setWatchList(bool);
            setFilterChanged(true);
          }}
          watchList={watchList}
          openAdvancedFilters={handleAdvancedFiltersClick}
        />
      </ErrorBoundary>
      <div className={classes.valuationsMain}>
        <div className={classes.valuationsContainer}>
          <ErrorBoundary>
            <AssetList
              filters={filters}
              filterChanged={filterChanged}
              setAsset={setAsset}
              setFilterChanged={setFilterChanged}
              selectedAsset={selectedAsset}
              watchList={watchList}
              updateWatchList={updateWatchList}
              setUpdateWatchList={setUpdateWatchList}
              shoreStatusData={shoreStatusData}
            />
          </ErrorBoundary>
        </div>
        <div className={classes.valuationsCenterContainer}>
          <ValuationReports
            asset={selectedAsset}
            context={VALUATIONS_CONTEXT}
            setUpdateWatchList={setUpdateWatchList}
            reportsLoaded={reportsLoaded}
            setReportsLoaded={setReportsLoaded}
          />
        </div>
        <div className={classes.valuationsContainer}>
          <Sidebar asset={selectedAsset} context={VALUATIONS_CONTEXT} watchList={watchList} />
          <Support />
        </div>
      </div>
      <AdvancedFilters
        title='Advanced'
        context={VALUATIONS_CONTEXT}
        googleAnalyticsTag={VALUATIONS_GOOGLE_ANALYTICS_TAG}
        open={filtersModalOpen}
        setOpen={setFiltersModalOpen}
        defaultFilters={advancedFilters}
        clearFilters={() => setAdvancedFilters({ ...defaultFilters })}
        apply={setFilters}
        filterTypes={advancedFilterTypes}
        checkListFilterTypes={[
          SHORE_STATUSES_CHECK_LIST,
          DEVELOPMENT_STATUSES_CHECK_LIST,
          DEVELOPMENT_TYPES_CHECK_LIST,
        ]}
        filterItemClass={classes.filterItemAdvanced}
        minMaxFilterTypes={[NPV_MIN_MAX, RESERVES_MIN_MAX, PRODUCTION_MIN_MAX, RESERVOIR_MIN_MAX]}
        checkListItems={{
          developmentStatuses,
          developmentTypes: developmentTypeData?.developmentType || [],
          shoreStatuses: (shoreStatusData?.shoreStatus || []).filter(s => s.assetsCount > 0),
        }}
      />
    </Grid>
  );
};

export default Valuations;
